import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, today } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { PrintButton, PrintHeader } from "@/components/PrintList";
import { AgendarRevisionButton } from "@/components/AgendarRevision";

const NoAprobados = ({ centro }) => {
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/no-aprobados", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro, tick]);
  if (!rows?.length) return null;
  return (
    <div className="mb-6 fade-up no-print" data-testid="no-aprobados-section">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-2 text-sky-800">No aprobaron el tratamiento · {rows.filter((r) => !r.cita).length} sin revisión agendada</h3>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-96 overflow-y-auto">
        {rows.map((p) => (
          <div key={p.id} data-testid={`no-aprobado-${p.id}`} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
            <Link to={`/pacientes/${p.id}`} className="font-medium text-slate-900 hover:underline">{p.nombre}</Link>
            <span className="text-xs text-slate-500">{p.centro}{p.telefono ? ` · ${p.telefono}` : ""} · no aprobó {fmtDate(p.desde)}</span>
            <span className="ml-auto">{p.cita ? <span className="text-xs font-medium text-emerald-700" data-testid={`no-aprobado-cita-${p.id}`}>Revisión el {fmtDate(p.cita.fecha)} a las {p.cita.inicio}</span>
              : <AgendarRevisionButton patient={p} fecha={p.sugerida} onDone={() => setTick((t) => t + 1)} testId={`no-aprobado-agendar-${p.id}`} label={`Agendar revisión (${fmtDate(p.sugerida)})`} />}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const finMes = () => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const GRUPOS = [
  { id: "vencidas", label: "Vencidas", cls: "text-rose-700", fn: (p) => p.fecha_prox_revision < today() },
  { id: "mes", label: "Este mes", cls: "text-amber-700", fn: (p) => p.fecha_prox_revision >= today() && p.fecha_prox_revision < finMes() },
  { id: "proximas", label: "Más adelante", cls: "text-slate-600", fn: (p) => p.fecha_prox_revision >= finMes() },
];

const Row = ({ p, onEstado }) => (
  <tr data-testid={`revprog-row-${p.id}`} className="hover:bg-slate-50">
    <td className="px-3 py-2 font-[JetBrains_Mono] text-xs whitespace-nowrap">{fmtDate(p.fecha_prox_revision)}</td>
    <td className="px-3 py-2"><Link to={`/pacientes/${p.id}`} className="font-medium text-slate-900 hover:underline">{p.nombre}</Link>{p.codigo && <span className="ml-2 text-[11px] text-slate-400">{p.codigo}</span>}</td>
    <td className="px-3 py-2 text-xs text-slate-500">{p.centro}</td>
    <td className="px-3 py-2 text-xs text-slate-500">{p.telefono}</td>
    <td className="px-3 py-2 text-xs text-slate-500">{p.prox_revision_en}</td>
    <td className="px-3 py-2">
      <select data-testid={`revprog-estado-${p.id}`} className="field-select h-8 text-xs py-0" value={p.estado_prox_revision || ""} onChange={(e) => onEstado(p, e.target.value)}>
        <option value="">Pendiente de citar</option>
        <option value="CITA DADA">Cita dada</option>
        <option value="HECHA">Hecha</option>
      </select>
    </td>
  </tr>
);

export default function RevisionesProgramadas() {
  const { centro } = useCentro();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/revisiones-programadas", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);
  const onEstado = async (p, estado) => {
    await api.post(`/patients/${p.id}/prox-revision`, { estado });
    toast.success(estado === "HECHA" ? "Revisión hecha. Si toca otra, apúntala en su ficha." : "Guardado");
    load();
  };
  return (
    <div data-testid="revisiones-programadas-page">
      <div className="no-print"><PageTitle title="Revisiones programadas" subtitle="Pacientes con próxima revisión apuntada, aunque no necesiten tratamiento"><PrintButton /></PageTitle></div>
      <PrintHeader title="Revisiones programadas" n={rows?.length} />
      <NoAprobados centro={centro} />
      {rows === null && <p className="text-sm text-slate-400">Cargando…</p>}
      {rows?.length === 0 && <p className="text-sm text-slate-400" data-testid="revprog-empty">No hay revisiones programadas.</p>}
      {rows && GRUPOS.map((g) => {
        const list = rows.filter(g.fn);
        if (!list.length) return null;
        return (
          <div key={g.id} className="mb-6 fade-up" data-testid={`revprog-grupo-${g.id}`}>
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${g.cls}`}>{g.label} · {list.length}</h3>
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">{list.map((p) => <Row key={p.id} p={p} onEstado={onEstado} />)}</tbody></table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
